import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';

import {map} from 'rxjs/operators';
import {Observable} from 'rxjs';

import {CookieService} from 'ngx-cookie-service';
import {Pageable, Individual} from 'src/app/models';

@Injectable({
    providedIn: 'root'
})
export class DataService {
    constructor(private http: HttpClient, private cookieService: CookieService) {
    }

    getUserInfo() {
        return this.http.get('api/v1/userInfo')
            .pipe(map((userInfo: any) => {
                // Get TRUE isAdmin
                const isAdmin = userInfo.authorities.reduce((acc: boolean, cur: any) => (cur.authority === 'ROLE_alerting-admin') ? true : acc, false);

                // Set some cookies based on TRUE isAdmin
                if (!isAdmin) {
                    this.cookieService.delete('isAdmin');
                    this.cookieService.delete('adminOn');
                } else {
                    this.cookieService.set('isAdmin', isAdmin);
                    if (!this.cookieService.check('adminOn')) {
                        this.cookieService.set('adminOn', isAdmin);
                    }
                }

                // Set userInfo.isAdmin as combo of True Admin + Toggled Admin On/Off
                userInfo.isAdmin = (isAdmin && (this.cookieService.check('adminOn') ? this.cookieService.get('adminOn') === 'true' : true));
                return userInfo;
            }));
    }

    // This service returns the list of associates based on search id(lastname/associateId/email).
    getAssociateNames(val) {
        return this.http.get('api/v1/individuals/ad?id=' + encodeURIComponent(val));
    }

    // Find Associate by EXACT match
    getIndividualAndAssociateExactMatchLookup(val): Observable<Pageable<Individual[]>> {
        return this.http.get<Pageable<Individual[]>>('api/v1/individuals/associates?exact=true&identifier=' + encodeURIComponent(val));
    }

    // This service returns a combined list of individualDTO and converted individualDTO from the associate record based on search identifier.
    getIndividualAndAssociateNames(val): Observable<Pageable<Individual[]>> {
        return this.http.get<Pageable<Individual[]>>('api/v1/individuals/associates?identifier=' + encodeURIComponent(val) + '&includeContacts=True');
    }

    getIndividualAndAssociateNamesPage(val, page): Observable<Pageable<Individual>> {
        return this.http.get<Pageable<Individual>>('api/v1/individuals/associates?identifier=' + encodeURIComponent(val) + '&limit=' + page);
    }

    // This service returns the list of AppMap applications by appId or name search string.
    getApplicationNames(val) {
        val = encodeURIComponent(val);
        return this.http.get('api/v1/applications?identifier=' + val)
            .pipe(map(applications => this.mapApplications(applications)));
    }

    mapApplications(applications) {
        const appsUIFormat = [];
        applications.forEach(application => {
            const formattedApplication = {
                application_id: application.id,
                application_name: application.name
            };
            appsUIFormat.push(formattedApplication);
        });
        return appsUIFormat;
    }

    getClientNames(val: string): any {
        val = encodeURIComponent(val);
        return this.http.get('api/v1/clients?identifier=' + val)
            .pipe(map(clients => this.mapClients(clients)));
    }

    mapClients(clients) {
        const clientsUIFormat = [];
        clients.forEach(client => {
            const formattedClient = {
                client_id: client.u_party_id,
                client_name: client.name
            };
            clientsUIFormat.push(formattedClient);
        });
        return clientsUIFormat;
    }

    updateSetting(settings) {
        return this.http.put('api/v1/settings', settings);
    }

    getSetting(settingName) {
        return this.http.get(`api/v1/settings/${settingName}`);
    }

    getSettings(): any {
        return this.http.get('api/v1/settings');
    }

    refreshSettingsCache() {
        return this.http.post('api/v1/settings/refresh', null, {responseType: 'text'});
    }

    getDeviceNameAvailability(name): any {
        return this.http.get('api/v1/admin/device-name?name=' + encodeURIComponent(name));
    }
}
